// SlashCommandAutocomplete.jsx — Floating dropdown shown when a Composer
// detects a `/cmd` prefix. Keyboard: Tab/Enter complete · ↑/↓ navigate ·
// Esc close. Mouse: click to pick.
//
// Usage:
//   const ref = useRef(null);
//   const [input, setInput] = useState('');
//   <textarea ref={ref} value={input} onChange={...} />
//   <SlashCommandAutocomplete
//     input={input}
//     anchorRef={ref}
//     onPick={(cmd) => { setInput(cmd.slash_command + ' '); ref.current.focus(); }}
//   />

import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useFilteredCommands, activeSlashTokenRange } from '../../hooks/useSlashCommands.js';
import { pluginColor, withAlpha } from '../../lib/pluginColor.js';

const HANDLER_LABEL = {
  prompt_template: 'Template',
  mcp_invoke: 'MCP',
  agent_dispatch: 'Agent',
  skill_invoke: 'Skill',
};

// v4 de-glass (Cursor IDE parity): flat surface popover — no blur, no specular.
// Scoped to [data-apolo-slash] so it never leaks. Row motion still drops under
// reduced-motion (handled elsewhere).
const SLASH_GLASS_STYLE = `
[data-apolo-slash]{
  border-radius:10px;
  background:#1c1c1c;
  border:1px solid #2a2a2a;
  box-shadow:0 10px 30px rgba(0,0,0,0.45);
  scrollbar-width:thin;scrollbar-color:rgba(255,255,255,0.15) transparent;
}
`;

export default function SlashCommandAutocomplete({
  input,
  onPick,
  onDismiss,
  anchorRef,
  maxItems = 8,
}) {
  // Caret-aware (bug do Pedro 08-17): rastreia o cursor do textarea (anchorRef)
  // pra detectar/trocar um /skill digitado NO MEIO da frase — não só no fim. Sem
  // isso, `input` (texto inteiro) só disparava o popup quando o token era a última
  // coisa da linha. Eventos: input/keyup/click/select/focus cobrem digitar, mover
  // com setas e clicar. null = fim do texto (retrocompatível).
  const [caret, setCaret] = useState(null);
  useEffect(() => {
    const el = anchorRef?.current;
    if (!el) return undefined;
    const sync = () => setCaret(el.selectionStart);
    for (const ev of ['input', 'keyup', 'click', 'select', 'focus']) el.addEventListener(ev, sync);
    sync();
    return () => { for (const ev of ['input', 'keyup', 'click', 'select', 'focus']) el.removeEventListener(ev, sync); };
  }, [anchorRef]);

  const { commands, detected, loading } = useFilteredCommands(input, caret);
  const [activeIdx, setActiveIdx] = useState(0);
  const listRef = useRef(null);
  const visible = detected !== null && commands.length > 0;

  // Faixa do /token ativo (no cursor) — passada ao onPick pra que o composer
  // troque EXATAMENTE o token e preserve o texto depois do cursor.
  const pick = (cmd) => onPick?.(cmd, activeSlashTokenRange(input, caret));

  // Reset active item when filtered list changes.
  useEffect(() => {
    setActiveIdx(0);
  }, [commands.length]);

  // Bug "o / parou de funcionar": com o menu aberto, o foco às vezes escapava do
  // textarea (ia pro body) e a digitação MORRIA — o usuário digitava e nada
  // acontecia. Guard conservador: enquanto o popup está visível, se o foco está
  // no body ou DENTRO do portal do menu, devolve pro textarea (anchorRef). Nunca
  // rouba foco de outro input legítimo.
  useEffect(() => {
    if (!visible) return;
    const el = anchorRef?.current;
    if (!el || typeof document === 'undefined') return;
    const ae = document.activeElement;
    const inMenu = !!(ae && typeof ae.closest === 'function' && ae.closest('[data-composer-menu-portal]'));
    if (ae !== el && (ae === document.body || ae == null || inMenu)) el.focus();
  }, [visible, commands.length, anchorRef]);

  // Keyboard handlers em CAPTURE (3º arg true) — críticos pra rodarem ANTES do
  // onKeyDown do <textarea> do composer (React fia no root, fase bubble). Sem
  // isso, apertar Enter pra ESCOLHER a skill caía primeiro no handleSubmit do
  // composer e "dava play" na mensagem; o onPick só rodava depois. Com capture
  // + stopPropagation, quando o popup está aberto o menu consome a tecla e o
  // composer nem a vê. Só intercepta as 5 teclas de navegação e só enquanto
  // `visible` — digitação normal e Enter sem popup passam direto pro textarea.
  useEffect(() => {
    if (!visible) return undefined;
    function onKey(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setActiveIdx((i) => Math.min(commands.length - 1, i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setActiveIdx((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        const cmd = commands[activeIdx];
        if (cmd) onPick?.(cmd, activeSlashTokenRange(input, caret));
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onDismiss?.();
      }
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [visible, commands, activeIdx, onPick, onDismiss, input, caret]);

  if (!visible) {
    return null;
  }

  const slice = commands.slice(0, maxItems);

  return (
    <>
      <style>{SLASH_GLASS_STYLE}</style>
      <div
        role="listbox"
        aria-label="Slash commands"
        data-apolo-slash=""
        className="z-40 max-h-[24rem] w-[min(30rem,92vw)] overflow-y-auto p-1.5"
        ref={listRef}
      >
        {loading && (
          <div className="px-3 py-6 text-center text-[11px] text-white/40">Carregando comandos…</div>
        )}
        {!loading &&
          slice.map((cmd, idx) => {
            const isActive = idx === activeIdx;
            // Color = identity: each plugin family gets a stable hue. The hue
            // is a quiet accent (bar + slash + active tint) — never a flood.
            const color = pluginColor(cmd.plugin_slug);
            return (
              <button
                key={cmd.slash_command}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => pick(cmd)}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIdx(idx)}
                data-active={isActive ? 'true' : undefined}
                className="flex w-full items-center gap-3 rounded-[14px] px-3 py-2.5 text-left transition hover:bg-white/[0.04] motion-reduce:transition-none"
                style={
                  isActive
                    ? {
                        backgroundColor: withAlpha(color, 0.12),
                        boxShadow: `inset 0 0 0 1px ${withAlpha(color, 0.22)}, 0 6px 20px ${withAlpha(color, 0.1)}`,
                      }
                    : undefined
                }
              >
                <span
                  aria-hidden="true"
                  className="h-7 w-[3px] shrink-0 rounded-full"
                  style={{ backgroundColor: color, boxShadow: `0 0 10px ${withAlpha(color, 0.5)}` }}
                />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-[13px] font-medium leading-tight text-white/95">
                    {cmd.display_name}
                  </span>
                  <span className="flex min-w-0 items-baseline gap-1.5">
                    <code className="truncate font-mono text-[11px]" style={{ color: withAlpha(color, 0.9) }}>
                      {cmd.slash_command}
                    </code>
                    {cmd.plugin_display_name && (
                      <span className="shrink-0 text-[10px] text-white/35">· {cmd.plugin_display_name}</span>
                    )}
                  </span>
                </span>
                <span
                  className="shrink-0 rounded-md px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider"
                  style={{ color: withAlpha(color, 0.85), backgroundColor: withAlpha(color, 0.12) }}
                >
                  {HANDLER_LABEL[cmd.handler_type] || cmd.handler_type}
                </span>
                <span
                  aria-hidden="true"
                  className={clsx('shrink-0 text-[11px] transition-opacity', isActive ? 'text-white/40 opacity-100' : 'opacity-0')}
                >
                  ↵
                </span>
              </button>
            );
          })}
        {!loading && commands.length > maxItems && (
          <div className="mt-1 flex items-center justify-between border-t border-white/[0.06] px-3 pb-1 pt-2 text-[10px] text-white/35">
            <span>+{commands.length - maxItems} resultados — continue digitando</span>
            <span className="flex items-center gap-1">
              <kbd className="rounded bg-white/[0.06] px-1.5 py-0.5 text-white/50">↑↓</kbd>
              <kbd className="rounded bg-white/[0.06] px-1.5 py-0.5 text-white/50">↵</kbd>
              <kbd className="rounded bg-white/[0.06] px-1.5 py-0.5 text-white/50">esc</kbd>
            </span>
          </div>
        )}
      </div>
    </>
  );
}
